import React, { useCallback, useRef, useState } from 'react';
import './index.scss';
import { Drawer, Iconfont } from '@umu/umu-design';
import { DrawerProps } from '@umu/umu-design/es/components/Drawer';
import { COURSE_ITEM_TYPE, EUserSetupType } from './enum';
import {
    ICourseInfo,
    IElementListItem,
    IElementListResponse,
    getCourseInfo,
    getElementList,
    updateUserSetup,
} from './api';
import _get from 'lodash/get';
import { ChapterCard } from './components/chapterCard';
import { SessionCard } from './components/sessionCard';
import { useBoolean, useInfiniteScroll, useMount, useUpdateEffect, useSize } from 'ahooks';
import { _lang, lang } from '@umu/ukit-lang';
import { getBool, preventScrolling } from './util';
import { ObserverIntersectionView } from './components/observerIntersectionView';
import { components } from 'common';
import { getMiniAppSdk, isFromCourseMiniApp } from '../../../../@utils';
const { Loading } = components;

interface IProps extends DrawerProps {
    parentId: string;
}
export const COURSE_CATALOG_DRAWER_ID = 'umu-course-catalog-drawer';
export const CourseCatalogDrawer = (props: IProps) => {
    const { parentId, ...otherProps } = props;
    const { visible } = otherProps;
    const [courseInfo, setCourseInfo] = useState<ICourseInfo>();

    const [sortDown, { toggle: _setSortDown, set: setSort }] = useBoolean(false);

    useMount(() => {
        getCourseInfo({ group_id: parentId }).then((res) => {
            setCourseInfo(res);
            setSort(res.session_sort_flag == 1);
        });
    });

    const allowBodyScroll = useRef<(() => void) | null>(null);
    useUpdateEffect(() => {
        if (visible) {
            allowBodyScroll.current = preventScrolling();
        } else {
            if (allowBodyScroll.current) {
                allowBodyScroll.current();
            }
        }
    }, [visible]);

    const { data, loading, reload, noMore, loadingMore, loadMore } = useInfiniteScroll<IElementListResponse>(
        (newData: any) => {
            const newPage = Number(_get(newData, 'page_info.current_page', 0)) + 1;
            return getElementList({
                parent_id: parentId,
                size: 20,
                page: newPage,
                key_word: '',
                get_draft: 0,
            });
        },
        {
            isNoMore: (newData: any) => {
                if (newData && newData.page_info) {
                    const { current_page, total_page_num } = newData.page_info;
                    return current_page >= total_page_num;
                }
                return false;
            },
        },
    );

    const list = data?.list || [];
    const total = _get(data, 'page_info.group_count.session_count', 0);

    const setSortDown = async () => {
        _setSortDown();
        await updateUserSetup({
            option_type: EUserSetupType.SESSION_SORT,
            option_value: !sortDown ? 1 : 2,
            option_obj_id: parentId,
        });
        reload();
    };

    const onLastItemDisplay = useCallback(
        (visible: boolean) => {
            const needMore = visible && !noMore;
            if (loadMore && needMore) {
                loadMore();
            }
        },
        [noMore, loadMore],
    );

    const renderItem = (item: IElementListItem) => {
        const { item_type } = item;
        if (item_type === COURSE_ITEM_TYPE.CHAPTER) {
            return <ChapterCard item={item} courseInfo={courseInfo} groupId={parentId}></ChapterCard>;
        }
        return <SessionCard item={item} courseInfo={courseInfo}></SessionCard>;
    };
    const bodyWidth = useSize(document.body);

    return (
        <Drawer
            placement="bottom"
            closable={false}
            className="courseCatalogDrawer"
            height="75vh"
            getContainer={() => {
                return document.querySelector('.doc') || document.body;
            }}
            contentWrapperStyle={{
                width: bodyWidth?.width || '100vw',
            }}
            {...otherProps}
        >
            {loading || !courseInfo ? (
                <Loading />
            ) : (
                <>
                    <div className="header">
                        <div className="filter">
                            <div className="title flex--start--center">
                                {lang('目录') + (_lang('en') ? ' ' : '')}({total})
                                {courseInfo.shareUrl && (
                                    <a
                                        href={'javascript:;'}
                                        className="sub-title flex--start--center"
                                        onClick={()=>{
                                            if(!isFromCourseMiniApp()){
                                                window.location.href=courseInfo.shareUrl;
                                            }else{
                                                // 小程序中打开时，必定会经过目录页再打开小节页，所以这里直接back即可
                                                getMiniAppSdk().navigateBack();
                                            }
                                        }}
                                    >
                                        <Iconfont type="SmallBack" />
                                        {lang('课程首页')}
                                    </a>
                                )}
                            </div>
                            <Iconfont
                                className="sort-icon"
                                type="SortDown"
                                onClick={setSortDown}
                                style={{
                                    transform: sortDown ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.3s',
                                }}
                            />
                        </div>
                        {getBool(courseInfo.setup?.is_emigrated) ? (
                            <div className="tip">{lang('已开启闯关模式，完成上一小节后将自动解锁下一小节。')}</div>
                        ) : null}
                    </div>
                    <div className="catalog">
                        {list.map((item: any, index: any) => {
                            const isLastItem = index == list.length - 1;
                            if (isLastItem) {
                                return (
                                    <ObserverIntersectionView
                                        key={index}
                                        className="item"
                                        onDisplay={onLastItemDisplay}
                                    >
                                        {renderItem(item)}
                                    </ObserverIntersectionView>
                                );
                            }
                            return (
                                <div key={index} className="item">
                                    {renderItem(item)}
                                </div>
                            );
                        })}
                        <Loading show={loadingMore} className="loading-more" />
                    </div>
                </>
            )}
        </Drawer>
    );
};

